<template>
  <div class="workflow-canvas-container">
    <!-- Top-level Tab Navigation -->
    <div class="top-level-tabs" :aria-disabled="isAIGenerating || showPreview">
      <div class="tab-navigation-bar">
        <!-- Workflow Type Chip & Back Button -->
        <div class="workflow-header-left" style="align-items: center;">
          <WorkflowTypeChip
            :is-program="isProgram"
            :is-disabled="isWorkflowActive && hasAssignees"
            @click="handleWorkflowTypeChipClick"
          />
          <button class="back-button" @click="goBackToList">
            <svg xmlns="http://www.w3.org/2000/svg" class="shrink-0 block" width="28" height="28" viewBox="0 0 28 28" fill="none">
              <path d="M16.2283 18.1883L14.5833 19.8333L8.75 14L14.5833 8.16667L16.2283 9.81167L13.2183 12.8333H24.5V15.1667H13.2183L16.2283 18.1883ZM5.83333 3.5H22.1667C23.4617 3.5 24.5 4.55 24.5 5.83333V10.5H22.1667V5.83333H5.83333V22.1667H22.1667V17.5H24.5V22.1667C24.5 23.45 23.4617 24.5 22.1667 24.5H5.83333C4.55 24.5 3.5 23.45 3.5 22.1667V5.83333C3.5 4.55 4.55 3.5 5.83333 3.5Z" fill="#374957"/>
            </svg>
            <span class="back-button-text">Back to Workbench</span>
          </button>
        </div>

        <!-- Workflow Title & Description -->
        <div class="workflow-title-section" style="margin-left: 24px; flex: 0 1 auto; min-width: 0;">
          <div class="title-row">
            <input
              v-model="workflowTitle"
              type="text"
              class="workflow-title-input"
              placeholder="Untitled Workflow"
              maxlength="255"
              :disabled="hasAssignees"
              style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
            />
            <button class="edit-title-btn">
              <img src="@/assets/MyWorkBench/Workflow/card_action_edit.svg"/>
            </button>
          </div>
          <textarea
            v-model="workflowDescription"
            type="textarea"
            class="workflow-description-input"
            placeholder="Edit Workflow description here ..."
            maxlength="1000"
            :disabled="hasAssignees"
          />
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons-group">
          <div class="primary-buttons-row">
            <ResponsiveButton
              button_type="outlined"
              built_in_theme="dark"
              size="small"
              :display_name="isProgram ? 'Manage Assignees' : 'Manage Usage'"
              customized_class="btn-manage-usage"
              @click-button="handleManageButtonClick"
            >
              <template #prefix>
                <img src="@/assets/MyWorkBench/Workflow/manage_usage.svg" alt="" />
              </template>
            </ResponsiveButton>
            <ResponsiveButton
              button_type="outlined"
              built_in_theme="dark"
              size="small"
              display_name="Copy to New"
              customized_class="btn-copy-new"
              @click-button="copyToNew"
            >
              <template #prefix>
                <img src="@/assets/MyWorkBench/Workflow/copy_all.svg" alt="" />
              </template>
            </ResponsiveButton>
            <ResponsiveButton
              button_type="filled"
              built_in_theme="dark"
              size="small"
              :display_name="isProgram ? 'Save Program' : 'Save Workflow'"
              :disabled="hasAssignees"
              customized_class="btn-save-workflow"
              @click-button="saveWorkflow"
            >
              <template #prefix>
                <img src="@/assets/MyWorkBench/Workflow/save_to_workflow.svg" alt="" />
              </template>
            </ResponsiveButton>
          </div>
          <ResponsiveButton
            button_type="filled"
            built_in_theme="sky"
            size="small"
            display_name="Run Test"
            :disabled="isAIGenerating || showPreview || workflowStatus === 'draft'"
            customized_class="btn-run-test"
            @click-button="runTest"
          >
            <template #prefix>
              <img src="@/assets/MyWorkBench/Workflow/run_test.svg" alt="" />
            </template>
          </ResponsiveButton>
        </div>
      </div>

    </div>

    <!-- Main Content Area -->
    <div class="canvas-content">
      <!-- Three Column Layout -->
      <div class="three-column-layout">
        <!-- Left Panel: Block Palette -->
        <div class="left-panel block-palette" :class="{ collapsed: isLeftPanelCollapsed }" :aria-disabled="isAIGenerating || showPreview">
          <button class="panel-collapse-btn left" @click="isLeftPanelCollapsed = !isLeftPanelCollapsed">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" :class="{ 'rotate-180': !isLeftPanelCollapsed }">
              <path d="M6 12L10 8L6 4" stroke="#374957" stroke-width="2"/>
            </svg>
          </button>

          <div v-if="!isLeftPanelCollapsed" class="left-panel-content">
            <div class="w-full pt-2 bg-white">
	          <Tab
	            v-model="activePaletteTab"
	            type="underline"
	            :tabs="paletteTabOptions"
	          />
            </div>

          <!-- Palette Content -->
          <div class="palette-content">
            <p class="palette-description">
              {{ paletteDescriptions[activePaletteTab] }}
            </p>

            <!-- Logic Section Divider (only for Logic tab) -->
            <div v-if="activePaletteTab === 'logic'" class="logic-section-divider">
              <div class="divider-line"></div>
              <span class="divider-text">Basic Logic</span>
              <div class="divider-line"></div>
            </div>

            <!-- Block Grid -->
            <div class="block-grid">
              <button 
                v-for="block in paletteBlocks[activePaletteTab]" 
                :key="block.id"
                class="block-item"
                :class="{
                  'selected': activePaletteTab === 'triggers' && selectedTrigger === block.id,
                  'disabled': activePaletteTab === 'triggers' && (isTriggerDisabledForProgram(block.id) || (hasTriggerOnCanvas() && !isTriggerOnCanvas(block.id)))
                }"
                :draggable="(activePaletteTab === 'triggers' && !hasTriggerOnCanvas() && !isTriggerDisabledForProgram(block.id)) || activePaletteTab === 'logic' || activePaletteTab === 'actions'"
                :disabled="activePaletteTab === 'triggers' && (isTriggerDisabledForProgram(block.id) || (hasTriggerOnCanvas() && !isTriggerOnCanvas(block.id)))"
                @click="activePaletteTab === 'triggers' ? selectTrigger(block.id) : null"
                @dragstart="(activePaletteTab === 'triggers' || activePaletteTab === 'logic' || activePaletteTab === 'actions') ? onDragStart($event, block) : null"
                @mouseenter="hoveredBlockId = block.id"
                @mouseleave="hoveredBlockId = null"
              >
                <div 
                  class="block-icon" 
                  :style="{ 
                    backgroundColor: activePaletteTab === 'triggers' && selectedTrigger === block.id ? block.bgColorSelected : (activePaletteTab === 'triggers' && (isTriggerDisabledForProgram(block.id) || (hasTriggerOnCanvas() && !isTriggerOnCanvas(block.id))) ? '#F2F2F2' : block.bgColor)
                  }"
                >
                  <BlockIcon
                    :iconName="block.icon"
                    :isSelected="activePaletteTab === 'triggers' && selectedTrigger === block.id"
                    :isDisabled="activePaletteTab === 'triggers' && (isTriggerDisabledForProgram(block.id) || (hasTriggerOnCanvas() && !isTriggerOnCanvas(block.id)))"
                    :isHovered="hoveredBlockId === block.id && !(activePaletteTab === 'triggers' && (isTriggerDisabledForProgram(block.id) || (hasTriggerOnCanvas() && !isTriggerOnCanvas(block.id))))"
                    :category="activePaletteTab === 'triggers' ? 'trigger' : (activePaletteTab === 'actions' ? 'action' : 'logic')"
                  />
                </div>
                <span 
                  class="block-label"
                  :class="{
                    'label-selected': activePaletteTab === 'triggers' && selectedTrigger === block.id,
                    'label-disabled': activePaletteTab === 'triggers' && (isTriggerDisabledForProgram(block.id) || (hasTriggerOnCanvas() && !isTriggerOnCanvas(block.id)))
                  }"
                >{{ block.label }}</span>
              </button>
            </div>
          </div>
          </div>
        </div>

        <!-- Middle Panel: Canvas Area -->
        <div class="middle-panel canvas-area">
          <!-- Canvas -->
          <div class="workflow-canvas">
            <!-- Canvas Toolbar (Centered with absolute positioning) -->
            <div class="canvas-toolbar-centered">
              <ResponsiveButton button_type="outlined" built_in_theme="light" size="small" @click-button="autoLayout">
                <template #prefix>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M3 13V5C3 4.45 3.19583 3.97917 3.5875 3.5875C3.97917 3.19583 4.45 3 5 3H11V13H3ZM13 3H19C19.55 3 20.0208 3.19583 20.4125 3.5875C20.8042 3.97917 21 4.45 21 5V9H13V3ZM13 21V11H21V19C21 19.55 20.8042 20.0208 20.4125 20.4125C20.0208 20.8042 19.55 21 19 21H13ZM3 15H11V21H5C4.45 21 3.97917 20.8042 3.5875 20.4125C3.19583 20.0208 3 19.55 3 19V15ZM5 11H9V5H5V11ZM15 7H19V5H15V7ZM15 13V19H19V13H15ZM5 17V19H9V17H5Z" fill="currentColor"/>
                  </svg>
                </template>
                AutoLayout
              </ResponsiveButton>
              <div class="zoom-controls">
                <button class="zoom-btn" @click="zoomOut">−</button>
                <span class="zoom-level">{{ currentZoom }}%</span>
                <button class="zoom-btn" @click="zoomIn">+</button>
              </div>
            </div>
            
            <!-- VueFlow Canvas -->
            <VueFlow
              ref="vueFlowRef"
              v-model:nodes="nodes"
              v-model:edges="edges"
              class="vue-flow-canvas"
              :default-viewport="viewport"
              :default-edge-options="{ type: 'bezier' }"
              :min-zoom="0.25"
              :max-zoom="2"
              :snap-to-grid="true"
              :snap-grid="[15, 15]"
              :nodes-draggable="canvasOperable"
              :nodes-connectable="canvasOperable"
              :elements-selectable="canvasOperable"
              @nodes-change="onNodesChange"
              @edges-change="onEdgesChange"
              @connect="onConnect"
              @node-click="onNodeClick"
              @pane-click="onPaneClick"
              @drop="onDrop"
              @dragover="onDragOver"
            >
              <!-- Custom Node Templates - Using Backend Enum Values -->
              <!-- Trigger Nodes -->
              <template #node-order_update="nodeProps">
                <TriggerOrderUpdate v-bind="nodeProps" @delete="deleteNode(nodeProps.id)" @configure="handleNodeConfigure(nodeProps.id)"/>
              </template>
              <template #node-appointment_update="nodeProps">
                <TriggerOrderUpdate v-bind="nodeProps" @delete="deleteNode(nodeProps.id)" @configure="handleNodeConfigure(nodeProps.id)"/>
              </template>
              <template #node-document_update="nodeProps">
                <TriggerOrderUpdate v-bind="nodeProps" @delete="deleteNode(nodeProps.id)" @configure="handleNodeConfigure(nodeProps.id)"/>
              </template>
              <template #node-report_update="nodeProps">
                <TriggerOrderUpdate v-bind="nodeProps" @delete="deleteNode(nodeProps.id)" @configure="handleNodeConfigure(nodeProps.id)"/>
              </template>
              <template #node-task_update="nodeProps">
                <TriggerOrderUpdate v-bind="nodeProps" @delete="deleteNode(nodeProps.id)" @configure="handleNodeConfigure(nodeProps.id)"/>
              </template>
              <template #node-internal_notes_update="nodeProps">
                <TriggerOrderUpdate v-bind="nodeProps" @delete="deleteNode(nodeProps.id)" @configure="handleNodeConfigure(nodeProps.id)"/>
              </template>
              <template #node-new_patient_created="nodeProps">
                <TriggerOrderUpdate v-bind="nodeProps" @delete="deleteNode(nodeProps.id)" @configure="handleNodeConfigure(nodeProps.id)"/>
              </template>
              <template #node-manual="nodeProps">
                <TriggerManual v-bind="nodeProps" @delete="deleteNode(nodeProps.id)" @configure="handleNodeConfigure(nodeProps.id)"/>
              </template>
              <template #node-scheduled="nodeProps">
                <TriggerScheduled v-bind="nodeProps" @delete="deleteNode(nodeProps.id)" @configure="handleNodeConfigure(nodeProps.id)" />
              </template>
              <!-- Action Nodes -->
              <template #node-send_message="nodeProps">
                <ActionSendNotification v-bind="nodeProps" @delete="deleteNode(nodeProps.id)" @configure="handleNodeConfigure(nodeProps.id)" @edit="handleNodeConfigure(nodeProps.id)"/>
              </template>
              <template #node-make_appointment="nodeProps">
                <ActionAppointment v-bind="nodeProps" @delete="deleteNode(nodeProps.id)" @configure="handleNodeConfigure(nodeProps.id)" @edit="handleNodeConfigure(nodeProps.id)" />
              </template>
              <template #node-assign_task="nodeProps">
                <ActionAssignTask v-bind="nodeProps" @delete="deleteNode(nodeProps.id)" @configure="handleNodeConfigure(nodeProps.id)" @edit="handleNodeConfigure(nodeProps.id)" />
              </template>
              <template #node-send_document="nodeProps">
                <ActionSendDocuments v-bind="nodeProps" @delete="deleteNode(nodeProps.id)" @configure="handleNodeConfigure(nodeProps.id)" @edit="handleNodeConfigure(nodeProps.id)" />
              </template>
              <template #node-place_order="nodeProps">
                <ActionPlaceOrder v-bind="nodeProps" @delete="deleteNode(nodeProps.id)" @configure="handleNodeConfigure(nodeProps.id)" @edit="handleNodeConfigure(nodeProps.id)" @openProductMenu="handleOpenProductMenu(nodeProps.id)" />
              </template>
              <template #node-add_internal_notes="nodeProps">
                <ActionAddInternalNotes v-bind="nodeProps" @delete="deleteNode(nodeProps.id)" @configure="handleNodeConfigure(nodeProps.id)" @edit="handleNodeConfigure(nodeProps.id)" @openProductMenu="handleNodeConfigure(nodeProps.id)" />
              </template>

              <!-- Custom Edge Template for Logic Connection -->
              <template #edge-logicConnection="edgeProps">
                <CustomConnectionLogic v-bind="edgeProps" @logic-card-click="handleLogicClick" @logic-card-dropped="handleLogicCardDropped" />
              </template>

              <Background 
                pattern-color="#F2F2F2"
                :gap="20"
                variant="lines"
              />
              <Controls 
                :show-zoom="false"
                :show-fit-view="false"
                :show-interactive="false"
              />
            </VueFlow>
          </div>

          <!-- Workflow Test Panel -->
          <WorkflowTestPanel
            ref="testPanel"
            :current-workflow="workflowTitle"
            :is-running="isTestRunning"
            @toggle="handleTestPanelToggle"
          />
          
          <!-- AI Workflow Overlay -->
          <AIWorkflowOverlay
            :visible="showAIOverlay"
            :state="aiOverlayState"
            @close="handleCloseOverlay"
          />
          
          <!-- AI Workflow Preview -->
          <AIWorkflowPreview
            :visible="showPreview"
            @discard="handleDiscardAIWorkflow"
            @accept="handleAcceptAIWorkflow"
          />
        </div>

        <!-- Right Panel: Side Panel (Collapsible) -->
        <CanvasRightPanel
          ref="rightPanel"
          :selected-node="selectedNode"
          :nodes="nodes"
          :edges="edges"
          :saved-configurations="savedConfigurations"
          :workflow-title="workflowTitle"
          :workflow-description="workflowDescription"
          :show-preview="showPreview"
          :is-program="isProgram"
          :program-settings="programSettings"
          :total-order-cost="totalOrderCost"
          :has-assignees="hasAssignees"
          @toggle="handleRightPanelToggle"
          @save-configuration="handleSaveConfiguration"
          @remove-block="handleRemoveLogicBlock"
          @ai-workflow-generated="handleAIWorkflowGenerated"
          @ai-generation-start="handleAIGenerationStart"
          @ai-generating-overlay-show="handleAIGeneratingOverlayShow"
          @ai-generation-error="handleAIGenerationError"
          @ai-generation-end="handleAIGenerationEnd"
          @save-program-settings="handleProgramSettingsSave"
        />
      </div>
    </div>

    <!-- Manage Usage Dialog -->
    <ManageUsageDialog
      v-if="!isProgram"
      :visible="showManageUsageDialog"
      :workflow-id="workflowId"
      :workflow-status="originalWorkflowData?.status || 'draft'"
      @close="showManageUsageDialog = false"
      @save="handleManageUsageSave"
      @status-changed="handleWorkflowStatusChanged"
      @save-workflow-before-activate="handleSaveWorkflowBeforeActivate"
    />

    <!-- Manage Assignees Dialog -->
    <ManageAssigneesDialog
      v-if="isProgram"
      :visible="showManageAssigneesDialog"
      :program-id="workflowId"
      :workflow-status="workflowStatus"
      :max-enrollment="programSettings.maximum_enrollment"
      :is-program="isProgram"
      @close="showManageAssigneesDialog = false"
      @save="handleManageAssigneesSave"
      @status-changed="handleWorkflowStatusChanged"
      @save-workflow-before-activate="handleSaveWorkflowBeforeActivate"
    />

    <!-- Delete Node Dialog -->
    <DeleteNodeDialog
      :visible="showDeleteNodeDialog"
      :node-name="nodeToDelete?.name || 'Node'"
      @close="showDeleteNodeDialog = false"
      @delete="confirmDeleteNode"
    />

    <!-- Config Order Dialog -->
    <NewOrderDialog
      :visible="showConfigOrderDialog"
      :config-mode="true"
      :initial-items="configOrderNodeData?.items || []"
      @close="showConfigOrderDialog = false"
      @save="handleConfigOrderSave"
    />

    <!-- Workflow Validation Dialog -->
    <WorkflowValidationDialog
      :visible="showValidationDialog"
      :workflow-name="workflowTitle"
      @close="showValidationDialog = false"
      @validate="handleValidateWorkflow"
    />

    <!-- Convert Workflow Dialog -->
    <ConvertWorkflowDialog
      :visible="showConvertDialog"
      :workflow-name="workflowTitle"
      :is-program="isProgram"
      @close="showConvertDialog = false"
      @convert="handleConvertWorkflow"
    />
    <UnsavedChangesDialog
      v-model="showUnsavedChangesDialog"
      :loading="isUnsavedChangesSaving"
      loading-text="Saving workflow..."
      :message="unsavedChangesMessage"
      @save="handleUnsavedChangesSave"
      @discard="handleUnsavedChangesDiscard"
      @cancel="handleUnsavedChangesCancel"
    />
  </div>
</template>

<script>
import BlockIcon from './BlockIcon.vue';
import WorkflowTestPanel from './WorkflowTestPanel.vue';
import UnsavedChangesDialog from '@/components/UnsavedChangesDialog.vue';
import WorkflowButton from '@/components/workflow/WorkflowButton.vue';
import WorkflowTypeChip from '@/components/workflow/WorkflowTypeChip.vue';
import WorkflowValidationDialog from './Dialogs/WorkflowValidationDialog.vue';
import ConvertWorkflowDialog from './Dialogs/ConvertWorkflowDialog.vue';
import TriggerOrderUpdate from './NodesConnections/TriggerOrderUpdate.vue';
import TriggerManual from './NodesConnections/TriggerManual.vue';
import NodeEdgeTemplate from './NodeEdgeTemplate.json';
import TriggerScheduled from './NodesConnections/TriggerScheduled.vue';
import ActionSendNotification from './NodesConnections/ActionSendNotification.vue';
import ActionAppointment from './NodesConnections/ActionAppointment.vue';
import ActionAssignTask from './NodesConnections/ActionAssignTask.vue';
import ActionSendDocuments from './NodesConnections/ActionSendDocuments.vue';
import ActionPlaceOrder from './NodesConnections/ActionPlaceOrder.vue';
import ActionAddInternalNotes from './NodesConnections/ActionAddInternalNotes.vue';
import CustomConnectionLogic from './NodesConnections/CustomConnectionLogic.vue';
import ManageUsageDialog from './Dialogs/ManageUsageDialog.vue';
import ManageAssigneesDialog from './Dialogs/ManageAssigneesDialog.vue';
import DeleteNodeDialog from './Dialogs/DeleteNodeDialog.vue';
import NewOrderDialog from '@/views/MyPatient-demo/NewOrderDialog.vue';
import CanvasRightPanel from './CanvasRightPanel.vue';
import AIWorkflowOverlay from './AIWorkflowOverlay.vue';
import AIWorkflowPreview from './AIWorkflowPreview.vue';
import { VueFlow, useVueFlow, applyNodeChanges, applyEdgeChanges } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls } from '@vue-flow/controls';
import dagre from 'dagre';
import { notify } from '@/utils/notify';
import { mapGetters, mapActions } from 'vuex';
import { createStableSnapshot, registerUnsavedChangesHandler, clearUnsavedChangesHandler } from '@/utils/unsavedChanges';
import { getWorkflowApi, updateWorkflowApi, createWorkflowApi, copyWorkflowApi, getWorkflowAssigneesApi, upgradeWorkflowToProgramApi, downgradeProgramToWorkflowApi } from '@/api/workflowManage';
import { convertBackendToFrontend, transformWorkflowData } from '@/api/workflowManage';

export default {
  name: 'WorkflowCanvas',
  components: {
    BlockIcon,
    WorkflowTestPanel,
    UnsavedChangesDialog,
    WorkflowTypeChip,
    WorkflowValidationDialog,
    ConvertWorkflowDialog,
    CanvasRightPanel,
    AIWorkflowOverlay,
    AIWorkflowPreview,
    TriggerOrderUpdate,
    TriggerManual,
    TriggerScheduled,
    ActionSendNotification,
    ActionAppointment,
    ActionAssignTask,
    ActionSendDocuments,
    ActionPlaceOrder,
    ActionAddInternalNotes,
    CustomConnectionLogic,
    ManageUsageDialog,
    ManageAssigneesDialog,
    DeleteNodeDialog,
    NewOrderDialog,
    VueFlow,
    Background,
    Controls
  },
  
  provide() {
    return {
      isNodeButtonDisabled: () => this.hasAssignees || this.showPreview,
      isProgram: () => this.isProgram
    };
  },
  beforeRouteLeave(to, from, next) {
    this.handleRouteGuard(next);
  },
  beforeRouteUpdate(to, from, next) {
    this.handleRouteGuard(next);
  },
  
  setup() {
    const { project, zoomIn, zoomOut, setViewport, getViewport, fitView } = useVueFlow();
    return { project, vueFlowZoomIn: zoomIn, vueFlowZoomOut: zoomOut, setViewport, getViewport, fitView };
  },
  
  data() {
    return {
      activePaletteTab: 'triggers',
      isLeftPanelCollapsed: false,
      showManageUsageDialog: false,
      showManageAssigneesDialog: false,
      showDeleteNodeDialog: false,
      showConfigOrderDialog: false,
      showValidationDialog: false,
      showConvertDialog: false,
      nodeToDelete: null,
      configOrderNodeData: null,
      isTestRunning: false,
      workflowId: null,
      isNewWorkflow: false,
      isProgram: false,
      workflowTitle: '',
      workflowDescription: '',
      originalWorkflowData: null, // Store original workflow data from API
      previousWorkflow: null, // Store previous workflow before AI replacement (for undo)
      showAIOverlay: false, // Show AI workflow overlay
      aiOverlayState: 'loading', // 'loading', 'error'
      isAIGenerating: false, // Track AI workflow generation state
      showPreview: false, // Show preview mode with accept/discard
      selectedTrigger: null, // Track selected trigger (only one allowed)
      hoveredBlockId: null, // Track hovered block for icon color change
      selectedNode: null, // Track selected node for right panel editing
      editingNode: null, // Track editing node for right panel editing
      draggedBlock: null, // Track the block being dragged
      
      // VueFlow data
      nodes: [],
      edges: [],
      viewport: { x: 0, y: 0, zoom: 1 },
      
      // Saved configurations array - stores only confirmed saved data
      // Format: [{ id: 'node-1', type: 'node'|'edge', data: {...} }, ...]
      savedConfigurations: [],
      
      // Auto-save timer
      autoSaveTimer: null,
      
      paletteDescriptions: {
        triggers: 'Triggers are events related to your patients or practice that may need your attentions or response. They are causes of your workflow actions.',
        actions: 'Actions will be taken after a trigger happened. You can select multiple actions in a workflow and customized them, but all the actions  must be connect to the trigger to happen.',
        logic: 'Logic is how you want an action to take place if it\'s triggered. Wait, Conditional and Loop will activated the connected actions automatically, Review & Approval requires you to review and respond.'
      },
      
      paletteBlocks: {
        triggers: [
          { id: 't1', label: 'Order Update', icon: 'shopping_cart', bgColor: '#E5F9EF', bgColorSelected: '#004879', iconColor: '#20A7C1', iconColorDisabled: '#979797' },
          { id: 't2', label: 'Appointment Update', icon: 'event', bgColor: '#E5F9EF', bgColorSelected: '#004879', iconColor: '#20A7C1', iconColorDisabled: '#979797' },
          { id: 't3', label: 'Document/Form Update', icon: 'description', bgColor: '#E5F9EF', bgColorSelected: '#004879', iconColor: '#20A7C1', iconColorDisabled: '#979797' },
          { id: 't4', label: 'Report Update', icon: 'assignment', bgColor: '#E5F9EF', bgColorSelected: '#004879', iconColor: '#20A7C1', iconColorDisabled: '#979797' },
          { id: 't5', label: 'Task Update', icon: 'task', bgColor: '#E5F9EF', bgColorSelected: '#004879', iconColor: '#20A7C1', iconColorDisabled: '#979797' },
          { id: 't6', label: 'Internal Note Update', icon: 'account_circle', bgColor: '#E5F9EF', bgColorSelected: '#004879', iconColor: '#20A7C1', iconColorDisabled: '#979797' },
          { id: 't8', label: 'Manual', icon: 'build_circle', bgColor: '#E5F9EF', bgColorSelected: '#004879', iconColor: '#20A7C1', iconColorDisabled: '#979797' },
          { id: 't9', label: 'Scheduled', icon: 'schedule', bgColor: '#E5F9EF', bgColorSelected: '#004879', iconColor: '#20A7C1', iconColorDisabled: '#979797' }
        ],
        actions: [
          { id: 'a1', label: 'Send Message', icon: 'forward_to_inbox', bgColor: '#E4F3F5', iconColor: '#5587BC' },
          { id: 'a2', label: 'Appointment', icon: 'event', bgColor: '#E4F3F5', iconColor: '#5587BC' },
          { id: 'a3', label: 'Task', icon: 'task', bgColor: '#E4F3F5', iconColor: '#5587BC' },
          { id: 'a4', label: 'Document', icon: 'description', bgColor: '#E4F3F5', iconColor: '#5587BC' },
          { id: 'a5', label: 'Order', icon: 'shopping_cart', bgColor: '#E4F3F5', iconColor: '#5587BC' },
          { id: 'a6', label: 'Note', icon: 'note', bgColor: '#E4F3F5', iconColor: '#5587BC' }
        ],
        logic: [
          { id: 'l1', label: 'Wait', icon: 'timer', bgColor: '#E1D3F1', iconColor: '#6F4999' },
          { id: 'l2', label: 'Review & Approval', icon: 'approval', bgColor: '#E1D3F1', iconColor: '#6F4999' }
        ]
      },

      // Program settings data
      programSettings: {
        name: '',
        description: '',
        duration_type: '',
        start_date: '',
        end_date: '',
        durationValue: '',
        durationUnit: '',
        maximum_enrollment: null,
        price: 0
      },
      savedWorkflowSnapshot: '',
      showUnsavedChangesDialog: false,
      isUnsavedChangesSaving: false,
      pendingNavigationResolver: null,
      isBypassingUnsavedChangesGuard: false,
      skipNextRouteGuard: false,
      beforeUnloadHandler: null,
      unsavedChangesHandler: null,
    };
  },
  
  computed: {
    ...mapGetters('workbench/workflow', ['getWorkflowById']),
    currentWorkflow() {
      return {
        id: this.workflowId,
        title: this.workflowTitle,
        description: this.workflowDescription,
        nodes: this.nodes,
        edges: this.edges
      };
    },
    currentZoom() {
      // Get current zoom from VueFlow viewport
      const viewport = this.getViewport();
      return viewport ? Math.round(viewport.zoom * 100) : 100;
    },
    isWorkflowActive() {
      return this.originalWorkflowData?.status === 'active';
    },

    /**
     * Current workflow status for dialogs
     * Returns 'active' or 'draft'
     */
    workflowStatus() {
      return this.originalWorkflowData?.status || 'draft';
    },

    /**
     * Check if workflow has assignees
     * Canvas should be disabled if there are active patient assignments
     * This is a computed property that reacts to changes in originalWorkflowData
     */
    hasAssignees() {
      if (!this.originalWorkflowData) {
        return false;
      }
      
      // Check if workflow has assigned_patients_count
      const assignedCount = this.originalWorkflowData.assigned_patients_count || 0;
      return assignedCount > 0;
    },

    canvasOperable() {
      return (!this.hasAssignees) && (!this.showPreview);
    },
    hasUnsavedChanges() {
      return this.savedWorkflowSnapshot !== this.buildWorkflowSnapshot();
    },
    
    /**
     * Calculate total cost of all Place Order actions in the workflow
     * Sums up total_charge from each place_order node's payload
     */
    totalOrderCost() {
      if (!this.nodes || this.nodes.length === 0) {
        return 0;
      }
      
      return this.nodes.reduce((total, node) => {
        // Check if this is a place_order node
        if (node.type === 'place_order') {
          // Get the payload from savedData or data
          const nodeData = node.data?.savedData || node.data;
          const payload = nodeData?.payload;
          
          if (payload && payload.total_charge) {
            return total + (payload.total_charge || 0);
          }
        }
        return total;
      }, 0);
    },
    unsavedChangesMessage() {
      return `You have unsaved changes in this ${this.isProgram ? 'program' : 'workflow'}. Save before leaving this page?`;
    },

    paletteTabOptions() {
      return [
        { value: 'triggers', label: 'Triggers', icon: require('@/assets/MyWorkBench/Workflow/tab_triggers.svg') },
        { value: 'actions', label: 'Actions', icon: require('@/assets/MyWorkBench/Workflow/tab_actions.svg') },
        { value: 'logic', label: 'Logic', icon: require('@/assets/MyWorkBench/Workflow/tab_logic.svg') }
      ];
    },

    /**
     * Determine if canvas should be disabled
     * Canvas is disabled when workflow has assignees
     */
  },
  
  watch: {
    /**
     * Watch for changes in hasAssignees to show notifications
     * This will trigger when assignments change in ManageUsageDialog
     */
    hasAssignees(newValue, oldValue) {
      // Clear block editor when hasAssignees becomes true
      if (newValue) {
        this.editingNode = null;
        console.log('Block editor cleared due to workflow having assignees');
      }
      
      // Only show notification if value actually changed and it's not the initial load
      if (oldValue !== undefined && newValue !== oldValue) {
        if (newValue) {
          notify({
            title: 'Canvas Locked',
            message: 'This workflow now has active patient assignments. Canvas editing is disabled.',
            type: 'warning',
            duration: 5000,
            showClose: true
          });
        } else {
          notify({
            title: 'Canvas Unlocked',
            message: 'Patient assignments removed. Canvas editing is now enabled.',
            type: 'success',
            duration: 5000,
            showClose: true
          });
        }
      }
    },
    
    /**
     * Sync workflowTitle changes to programSettings immediately
     * Only for programs
     */
    workflowTitle(newValue) {
      if (this.isProgram && this.programSettings) {
        this.programSettings.name = newValue;
      }
    },
    
    /**
     * Sync workflowDescription changes to programSettings immediately
     * Only for programs
     */
    workflowDescription(newValue) {
      if (this.isProgram && this.programSettings) {
        this.programSettings.description = newValue;
      }
    },
    
    '$route.params.workflowId': {
      async handler(newWorkflowId, oldWorkflowId) {
        if (newWorkflowId && newWorkflowId !== oldWorkflowId) {
          console.log('Route changed to new workflow:', newWorkflowId);
          
          // Stop auto-save for old workflow
          this.stopAutoSave();
          
          // Update workflow ID
          this.workflowId = newWorkflowId;
          this.isNewWorkflow = newWorkflowId.startsWith('create_');
          
          // Load new workflow data
          if (!this.isNewWorkflow) {
            await this.loadWorkflow(newWorkflowId);
          } else {
            // Reset for new workflow
            this.workflowTitle = '';
            this.workflowDescription = '';
            this.nodes = [];
            this.edges = [];
            this.savedConfigurations = [];
            this.originalWorkflowData = null;
          }
          this.updateSavedWorkflowSnapshot();
          
          // Restart auto-save timer
          this.startAutoSave();
        }
      },
      immediate: false
    }
  },
  
  async mounted() {
    // Get workflow ID from route
    this.workflowId = this.$route.params.workflowId;
    this.isNewWorkflow = this.workflowId && this.workflowId.startsWith('create_');
    
    console.log('Workflow Canvas mounted:', {
      workflowId: this.workflowId,
      isNewWorkflow: this.isNewWorkflow
    });
    
    // Load workflow data if editing existing workflow
    if (this.workflowId && !this.isNewWorkflow) {
      await this.loadWorkflow(this.workflowId);
    }
    
    // Start auto-save timer (every 5 minutes)
    this.startAutoSave();

    this.updateSavedWorkflowSnapshot();
    this.beforeUnloadHandler = (event) => {
      if (!this.hasUnsavedChanges || this.isBypassingUnsavedChangesGuard) {
        return;
      }

      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);

    this.unsavedChangesHandler = async () => {
      if (this.isBypassingUnsavedChangesGuard || !this.hasUnsavedChanges) {
        return true;
      }

      return await new Promise((resolve) => {
        this.queueNavigationAction((shouldProceed) => {
          if (shouldProceed === false) {
            resolve(false);
            return;
          }

          this.skipNextRouteGuard = true;
          resolve(true);
        }, { isRouteGuard: true });
      });
    };
    registerUnsavedChangesHandler(this.unsavedChangesHandler);
  },
  
  beforeUnmount() {
    // Clear auto-save timer when component is destroyed
    this.stopAutoSave();
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    }
    clearUnsavedChangesHandler(this.unsavedChangesHandler);
  },
  
  methods: {
    /**
     * Generate a random 8-character alphanumeric ID
     * Format: 8 characters using 0-9 and a-f (like 8faee4af)
     * @returns {string} Random 8-character ID
     */
    generateRandomId() {
      return Array.from({ length: 8 }, () => 
        Math.floor(Math.random() * 16).toString(16)
      ).join('');
    },
    ...mapActions('workbench/workflow', ['addMyWorkflow']),
    serializeWorkflowNode(node) {
      return {
        id: node.id,
        type: node.type,
        position: node.position,
        data: node.data,
        sourcePosition: node.sourcePosition,
        targetPosition: node.targetPosition,
      };
    },
    serializeWorkflowEdge(edge) {
      return {
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle,
        targetHandle: edge.targetHandle,
        type: edge.type,
        animated: edge.animated,
        data: edge.data,
      };
    },
    buildWorkflowSnapshot() {
      return createStableSnapshot({
        workflowTitle: this.workflowTitle || '',
        workflowDescription: this.workflowDescription || '',
        isProgram: this.isProgram,
        nodes: this.nodes.map(this.serializeWorkflowNode),
        edges: this.edges.map(this.serializeWorkflowEdge),
        savedConfigurations: this.savedConfigurations,
        programSettings: this.isProgram ? this.programSettings : null,
      });
    },
    updateSavedWorkflowSnapshot() {
      this.savedWorkflowSnapshot = this.buildWorkflowSnapshot();
    },
    queueNavigationAction(action, options = {}) {
      if (this.showUnsavedChangesDialog || this.pendingNavigationResolver) {
        return;
      }

      this.pendingNavigationResolver = {
        action,
        isRouteGuard: Boolean(options.isRouteGuard),
      };
      this.showUnsavedChangesDialog = true;
    },
    handleRouteGuard(next) {
      if (this.unsavedChangesHandler) {
        if (this.skipNextRouteGuard) {
          this.skipNextRouteGuard = false;
        }
        next();
        return;
      }

      if (this.skipNextRouteGuard) {
        this.skipNextRouteGuard = false;
        next();
        return;
      }

      if (this.isBypassingUnsavedChangesGuard || !this.hasUnsavedChanges) {
        next();
        return;
      }

      this.queueNavigationAction(next, { isRouteGuard: true });
    },
    resolvePendingNavigation(shouldProceed) {
      const pendingNavigation = this.pendingNavigationResolver;
      this.pendingNavigationResolver = null;
      this.showUnsavedChangesDialog = false;

      if (!pendingNavigation) {
        return;
      }

      const { action, isRouteGuard } = pendingNavigation;

      if (!shouldProceed) {
        if (isRouteGuard) {
          action(false);
        }
        return;
      }

      this.isBypassingUnsavedChangesGuard = true;
      action();
      this.$nextTick(() => {
        this.isBypassingUnsavedChangesGuard = false;
      });
    },
    async handleUnsavedChangesSave() {
      this.isUnsavedChangesSaving = true;

      try {
        const wasSaved = await this.saveWorkflow();

        if (!wasSaved) {
          return;
        }

        this.resolvePendingNavigation(true);
      } finally {
        this.isUnsavedChangesSaving = false;
      }
    },
    handleUnsavedChangesDiscard() {
      this.resolvePendingNavigation(true);
    },
    handleUnsavedChangesCancel() {
      this.resolvePendingNavigation(false);
    },
    
    /**
     * Fetch current assignee count from API and update originalWorkflowData
     * This ensures we have the latest assignee information on initial canvas load
     * Works for both workflows and programs
     * @param {string} workflowId - Workflow/Program ID
     */
    async fetchAssigneeCount(workflowId) {
      try {
        const entityType = this.isProgram ? 'program' : 'workflow';
        console.log(`Fetching assignee count for ${entityType}:`, workflowId);
        const response = await getWorkflowAssigneesApi(workflowId);
        const assigneesData = response.data;
        
        // Update originalWorkflowData with current assignee count
        if (this.originalWorkflowData && assigneesData) {
          // Count all assignments (both 'active' and 'paused')
          const allAssignments = assigneesData.assignments || [];
          this.originalWorkflowData.assigned_patients_count = allAssignments.length;
          
          console.log(`Fetched assigned_patients_count for ${entityType}:`, this.originalWorkflowData.assigned_patients_count);
        }
      } catch (error) {
        console.error('Error fetching assignee count:', error);
        // If API fails, keep existing count from originalWorkflowData
      }
    },
    
    async copyToNew() {
      try {
        const entityType = this.isProgram ? 'program' : 'workflow';
        console.log(`Copying ${entityType} to new...`);
        
        if (!this.workflowId || this.isNewWorkflow) {
          notify({
            title: 'Error',
            message: `Cannot copy a new unsaved ${entityType}`,
            type: 'error',
            showClose: true
          });
          return;
        }
        
        // Call dedicated copy API endpoint
        // The API will preserve is_program flag and all program settings automatically
        const response = await copyWorkflowApi(this.workflowId);
        
        console.log(`${entityType} copy created successfully:`, response);
        
        const newWorkflow = response.data;
        
        if (!newWorkflow || !newWorkflow.id) {
          throw new Error(`Invalid ${entityType} response from API`);
        }
        
        // Add new workflow/program to the beginning of the store list
        this.addMyWorkflow(newWorkflow);
        
        notify({
          title: 'Success',
          message: `${entityType.charAt(0).toUpperCase() + entityType.slice(1)} copied successfully!`,
          type: 'success',
          showClose: true
        });

        const targetPath = `/my-workbench/automation/edit/${newWorkflow.id}`;

        if (this.hasUnsavedChanges) {
          this.queueNavigationAction(() => {
            this.isBypassingUnsavedChangesGuard = true;
            this.$router.push(targetPath).finally(() => {
              this.$nextTick(() => {
                this.isBypassingUnsavedChangesGuard = false;
              });
            });
          });
          return;
        }

        // Navigate to the new workflow/program canvas
        await this.$router.push(targetPath);
      } catch (error) {
        const entityType = this.isProgram ? 'program' : 'workflow';
        console.error(`Error copying ${entityType}:`, error);
        notify({
          title: 'Error',
          message: `Failed to copy ${entityType}: ${error.response?.data?.message || error.message}`,
          type: 'error',
          showClose: true
        });
      }
    },
    
    async loadWorkflow(workflowId) {
      try {
        console.log('Loading workflow:', workflowId);
        
        // Always fetch from API to get full workflow definition
        // (List API doesn't return detailed nodes/edges data)
        console.log('Fetching workflow from API...');
        const response = await getWorkflowApi(workflowId);
        const workflow = response.data;
        console.log('Workflow fetched from API:', workflow);
        
        if (!workflow) {
          throw new Error('Workflow not found');
        }
        
        // Store original workflow data from API
        this.originalWorkflowData = workflow;
        
        // Set isProgram from backend data
        this.isProgram = workflow.is_program || false;
        console.log('Workflow is_program:', this.isProgram);
        
        // Convert backend format to frontend canvas format
        const canvasData = convertBackendToFrontend(workflow);
        
        // Load workflow data into canvas
        this.workflowTitle = canvasData.workflowTitle;
        this.workflowDescription = canvasData.workflowDescription;
        this.nodes = canvasData.nodes;
        this.edges = canvasData.edges;
        this.savedConfigurations = [];
        
        // Load program settings if this is a program
        if (this.isProgram && canvasData.programSettings) {
          this.programSettings = canvasData.programSettings;
          console.log('Program settings loaded:', this.programSettings);
        }
        
        // Check if workflow has a trigger node and set selectedTrigger accordingly
        const existingTriggerNode = this.nodes.find(node => this.isTriggerNodeType(node.type));
        
        if (existingTriggerNode) {
          // Map backend enum node type to trigger block ID in left panel
          const triggerTypeToBlockId = {
            'order_update': 't1',
            'appointment_update': 't2',
            'document_update': 't3',
            'report_update': 't4',
            'task_update': 't5',
            'internal_notes_update': 't6',
            'new_patient_created': 't7',
            'manual': 't8',
            'scheduled': 't9'
          };
          this.selectedTrigger = triggerTypeToBlockId[existingTriggerNode.type];
          console.log('Trigger node found, selected trigger:', this.selectedTrigger, 'type:', existingTriggerNode.type);
        } else {
          this.selectedTrigger = null;
        }
        
        console.log('Workflow loaded successfully:', {
          title: this.workflowTitle,
          nodeCount: this.nodes.length,
          edgeCount: this.edges.length,
          selectedTrigger: this.selectedTrigger
        });
        
        // Fetch current assignee count to ensure canvas disabling is accurate
        await this.fetchAssigneeCount(workflowId);
        
        console.log('Canvas state - hasAssignees:', this.hasAssignees);
        
        // Switch right panel to editor tab when opening workflow
        if (this.$refs.rightPanel) {
          this.$refs.rightPanel.activeTab = 'editor';
        }
        
        // Fit view to show all nodes after loading
        this.$nextTick(() => {
          this.$refs.vueFlowRef.fitView({ padding: 0.2, duration: 300 });
        });
      } catch (error) {
        console.error('Error loading workflow:', error);
        notify({
          title: 'Error',
          message: `Failed to load workflow: ${error.message}`,
          type: 'error',
          showClose: true
        });
        // Navigate back to workflow list on error
        this.$router.push('/my-workbench/automation');
      }
    },
    
    // Drag and Drop handlers
    onDragStart(event, block) {
      // Prevent drag if workflow has assignees
      if (this.hasAssignees) {
        event.preventDefault();
        notify({
          title: 'Canvas Locked',
          message: 'Cannot add blocks. This workflow has active patient assignments.',
          type: 'warning',
          duration: 3000,
          showClose: true
        });
        return;
      }

      // Prevent drag during AI generation or preview
      if (this.isAIGenerating || this.showPreview) {
        event.preventDefault();
        notify({
          title: 'Canvas Locked',
          message: 'Cannot add blocks during AI generation or preview.',
          type: 'warning',
          duration: 3000,
          showClose: true
        });
        return;
      }
      
      // Store the dragged block data with type information
      this.draggedBlock = {
        ...block,
        blockType: this.activePaletteTab // 'triggers' or 'logic'
      };
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('application/vueflow', JSON.stringify(this.draggedBlock));
      console.log('Drag started:', this.draggedBlock);
    },
    
    onDragOver(event) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
    },
    
    onDrop(event) {
      event.preventDefault();

      // Prevent drop if workflow has assignees
      if (this.hasAssignees) {
        notify({
          title: 'Canvas Locked',
          message: 'Cannot add blocks. This workflow has active patient assignments.',
          type: 'warning',
          duration: 3000,
          showClose: true
        });
        this.draggedBlock = null;
        return;
      }

      // Prevent drop during AI generation or preview
      if (this.isAIGenerating || this.showPreview) {
        notify({
          title: 'Canvas Locked',
          message: 'Cannot add blocks during AI generation or preview.',
          type: 'warning',
          duration: 3000,
          showClose: true
        });
        this.draggedBlock = null;
        return;
      }
      
      if (!this.draggedBlock) {
        console.log('No dragged block data');
        return;
      }
      
      // Check if this is a logic block - logic blocks should only be dropped on connection slots
      if (this.draggedBlock.blockType === 'logic') {
        console.log('Logic block dropped on canvas - should be dropped on connection slot');
        notify({
          title: 'Warning',
          message: 'Please drop logic blocks on the connection slot (expanded drop zone)',
          type: 'warning',
          showClose: true
        });
        this.draggedBlock = null;
        return;
      }
      
      // Handle trigger node drop - check if a trigger node already exists (only one trigger allowed)
      if (this.draggedBlock.blockType === 'triggers') {
        const existingTriggerNode = this.nodes.find(node => this.isTriggerNodeType(node.type));
        
        if (existingTriggerNode) {
          notify({
            title: 'Warning',
            message: 'Only one trigger node is allowed per workflow',
            type: 'warning',
            showClose: true
          });
          this.draggedBlock = null;
          return;
        }
      }
      
      // Get the VueFlow container bounds
      const vueFlowBounds = event.currentTarget.getBoundingClientRect();
      
      // Calculate position relative to VueFlow container
      const x = event.clientX - vueFlowBounds.left;
      const y = event.clientY - vueFlowBounds.top;
      
      // Get current viewport (includes zoom and pan)
      const viewport = this.getViewport();
      
      // Project screen coordinates to flow coordinates (accounts for zoom and pan)
      const position = this.project({ x, y });
      
      // Map block ID to node type using backend enum values
      const blockTypeMap = {
        // Triggers - using backend enum values
        't1': { type: 'order_update', width: 280, height: 150 },
        't2': { type: 'appointment_update', width: 280, height: 150 },
        't3': { type: 'document_update', width: 280, height: 150 },
        't4': { type: 'report_update', width: 280, height: 150 },
        't5': { type: 'task_update', width: 280, height: 150 },
        't6': { type: 'internal_notes_update', width: 280, height: 150 },
        't7': { type: 'new_patient_created', width: 280, height: 150 },
        't8': { type: 'manual', width: 280, height: 150 },
        't9': { type: 'scheduled', width: 280, height: 150 },
        // Actions - using backend enum values
        'a1': { type: 'send_message', width: 220, height: 150 },
        'a2': { type: 'make_appointment', width: 220, height: 150 },
        'a3': { type: 'assign_task', width: 220, height: 150 },
        'a4': { type: 'send_document', width: 220, height: 150 },
        'a5': { type: 'place_order', width: 220, height: 150 },
        'a6': { type: 'add_internal_notes', width: 220, height: 150 }
      };
      
      const nodeConfig = blockTypeMap[this.draggedBlock.id];
      if (!nodeConfig) {
        console.error('Unknown block type:', this.draggedBlock.id);
        this.draggedBlock = null;
        return;
      }
      
      // Center the node at the drop position
      position.x -= nodeConfig.width / 2;
      position.y -= nodeConfig.height / 2;
      
      // Generate unique node ID with random 8-character alphanumeric string
      const nodeIdPrefix = this.draggedBlock.blockType === 'triggers' ? 'trigger' : 'action';
      const nodeId = `node-${nodeIdPrefix}-${this.generateRandomId()}`;
      
      // Get data template from NodeEdgeTemplate.json
      const template = NodeEdgeTemplate.nodes[nodeConfig.type];
      if (!template) {
        console.error('No template found for node type:', nodeConfig.type);
        this.draggedBlock = null;
        return;
      }
      
      // Deep clone the template to avoid reference issues
      const nodeData = JSON.parse(JSON.stringify(template));
      
      // Store the subtype in nodeData
      nodeData.subtype = nodeConfig.type;
      
      const newNode = {
        id: nodeId,
        type: nodeConfig.type,
        position: position,
        data: nodeData
      };
      
      // Add the new node to the canvas
      this.nodes.push(newNode);
      
      // If this is a trigger node, select it and disable other triggers
      if (this.draggedBlock.blockType === 'triggers') {
        this.selectedTrigger = this.draggedBlock.id;
      }
      
      console.log(`${this.draggedBlock.blockType === 'triggers' ? 'Trigger' : 'Action'} node dropped:`, newNode);
      
      // Show success notification
      const nodeTypeName = this.draggedBlock.blockType === 'triggers' ? 'Trigger' : 'Action';
      notify({
        title: 'Success',
        message: `${nodeTypeName} node added to workflow`,
        type: 'success',
        duration: 2000,
        showClose: true
      });
      
      // Automatically open the editor for the newly dropped node
      this.selectedNode = newNode;
      this.editingNode = newNode;
      this.$refs.rightPanel.switchToEditorTab();
      console.log('Editor opened for newly dropped node:', newNode.id);
      
      // Clear dragged block
      this.draggedBlock = null;
    },
    
    startAutoSave() {
      // Auto-save every 5 minutes (300000 milliseconds)
      this.autoSaveTimer = setInterval(() => {
        this.autoSaveWorkflow();
      }, 300000);
      console.log('Auto-save started: will save every 5 minutes');
    },
    
    stopAutoSave() {
      if (this.autoSaveTimer) {
        clearInterval(this.autoSaveTimer);
        this.autoSaveTimer = null;
        console.log('Auto-save stopped');
      }
    },
    
    async saveWorkflow() {
      try {
        console.log('Saving workflow...');
        
        if (!this.workflowId) {
          notify({
            title: 'Error',
            message: 'No workflow ID found',
            type: 'error',
            showClose: true
          });
          return false;
        }
        
        // Combine nodes with saved configurations
        const finalNodes = this.nodes.map(node => {
          const savedConfig = this.savedConfigurations.find(
            config => config.type === 'node' && config.id === node.id
          );
          
          if (savedConfig) {
            return {
              ...node,
              data: savedConfig.data
            };
          }
          
          return node;
        });
        
        // Combine edges with saved configurations
        const finalEdges = this.edges.map(edge => {
          const savedConfig = this.savedConfigurations.find(
            config => config.type === 'edge' && config.id === edge.id
          );
          
          if (savedConfig) {
            return {
              ...edge,
              data: savedConfig.data
            };
          }
          
          return edge;
        });
        
        // Convert frontend canvas data to backend API format
        console.log('saveWorkflow - About to transform, programSettings:', this.programSettings);
        const backendData = transformWorkflowData(
          {   workflowTitle: this.workflowTitle || 'Untitled Workflow',
              workflowDescription: this.workflowDescription || '',
              nodes: finalNodes,
              edges: finalEdges,
              savedConfigurations: this.savedConfigurations,
              programSettings: this.isProgram ? this.programSettings : null}
        );
        
        console.log('saveWorkflow - Converted workflow data:', backendData);
        console.log('saveWorkflow - Backend data includes program fields:', {
          duration: backendData.duration,
          price: backendData.price
        });
        
        // Call API to update workflow
        const response = await updateWorkflowApi(this.workflowId, backendData);
        
        console.log('Workflow saved successfully:', response);
        
        // Update original workflow data after successful save
        if (response.data) {
          this.originalWorkflowData = response.data;
        }
        this.updateSavedWorkflowSnapshot();
        
        notify({
          title: 'Success',
          message: 'Workflow saved successfully!',
          type: 'success',
          showClose: true
        });
        return true;
      } catch (error) {
        console.error('Error saving workflow:', error);
        notify({
          title: 'Error',
          message: `Failed to save workflow: ${error.response?.data?.message || error.message}`,
          type: 'error',
          showClose: true
        });
        return false;
      }
    },
    
    async autoSaveWorkflow() {
      try {
        console.log('Auto-saving workflow...');
        
        if (!this.workflowId) {
          console.log('No workflow ID, skipping auto-save');
          return;
        }
        
        // Skip API call if workflow is active and has assignees
        if (this.workflowStatus === 'active' && this.hasAssignees) {
          console.log('Auto-save: Workflow is active and has assignees, skipping API call but continuing auto-save timer');
          return;
        }
        
        // Combine nodes with saved configurations
        const finalNodes = this.nodes.map(node => {
          const savedConfig = this.savedConfigurations.find(
            config => config.type === 'node' && config.id === node.id
          );
          
          if (savedConfig) {
            return {
              ...node,
              data: savedConfig.data
            };
          }
          
          return node;
        });
        
        // Combine edges with saved configurations
        const finalEdges = this.edges.map(edge => {
          const savedConfig = this.savedConfigurations.find(
            config => config.type === 'edge' && config.id === edge.id
          );
          
          if (savedConfig) {
            return {
              ...edge,
              data: savedConfig.data
            };
          }
          
          return edge;
        });
        
        // Convert frontend canvas data to backend API format
        const backendData = transformWorkflowData(
          {   workflowTitle: this.workflowTitle || 'Untitled Workflow',
              workflowDescription: this.workflowDescription || '',
              nodes: finalNodes,
              edges: finalEdges,
              savedConfigurations: this.savedConfigurations,
              programSettings: this.isProgram ? this.programSettings : null}
        );
        
        console.log('Auto-save: Converted workflow data');
        
        // Call API to update workflow
        const response = await updateWorkflowApi(this.workflowId, backendData);
        
        // Update original workflow data after successful auto-save
        if (response.data) {
          this.originalWorkflowData = response.data;
        }
        this.updateSavedWorkflowSnapshot();
        
        console.log('Auto-save completed successfully:', {
          workflowId: this.workflowId,
          nodesCount: finalNodes.length,
          edgesCount: finalEdges.length,
          savedConfigsCount: this.savedConfigurations.length
        });
      } catch (error) {
        console.error('Error auto-saving workflow:', error);
        // Don't show error notification for auto-save failures
      }
    },

    /**
     * Handle program settings save
     * @param {Object} data - Program settings data
     */
    async handleProgramSettingsSave(data) {
      console.log('handleProgramSettingsSave - Received data:', data);
      console.log('handleProgramSettingsSave - Before update, programSettings:', this.programSettings);
      
      // Update local state
      this.programSettings = { ...data };
      
      // Sync name and description back to workflow title/description
      if (data.name !== undefined) {
        this.workflowTitle = data.name;
      }
      if (data.description !== undefined) {
        this.workflowDescription = data.description;
      }
      
      console.log('handleProgramSettingsSave - After update, programSettings:', this.programSettings);

      // Save whole workflow with updated program settings
      await this.saveWorkflow();
    },

    goBackToList() {
      this.$router.push('/my-workbench/automation');
    },
    
    selectTrigger(triggerId) {
      // Only allow one trigger to be selected
      this.selectedTrigger = triggerId;
    },
    
    isTriggerDisabled(triggerId) {
      // Disable triggers if one is already on canvas (except the one that's on canvas)
      return this.hasTriggerOnCanvas() && !this.isTriggerOnCanvas(triggerId);
    },
    
    // Helper method to check if a node type is a trigger
    isTriggerNodeType(nodeType) {
      const triggerTypes = [
        'order_update', 'appointment_update', 'document_update',
        'report_update', 'task_update', 'internal_notes_update',
        'new_patient_created', 'manual', 'scheduled'
      ];
      return triggerTypes.includes(nodeType);
    },
    
    // Helper method to check if a node type is an action
    isActionNodeType(nodeType) {
      const actionTypes = [
        'send_message', 'make_appointment', 'assign_task',
        'send_document', 'place_order', 'add_internal_notes'
      ];
      return actionTypes.includes(nodeType);
    },
    
    hasTriggerOnCanvas() {
      // Check if any trigger node exists on the canvas
      return this.nodes.some(node => this.isTriggerNodeType(node.type));
    },
    
    isTriggerOnCanvas(triggerId) {
      // Check if this specific trigger is on the canvas using backend enum values
      const triggerMap = {
        't1': 'order_update',
        't2': 'appointment_update',
        't3': 'document_update',
        't4': 'report_update',
        't5': 'task_update',
        't6': 'internal_notes_update',
        't7': 'new_patient_created',
        't8': 'manual',
        't9': 'scheduled'
      };
      
      const triggerType = triggerMap[triggerId];
      if (!triggerType) return false;
      
      return this.nodes.some(node => node.type === triggerType);
    },
    
    isTriggerDisabledForProgram(triggerId) {
      // For program workflows, only Manual trigger (t8) is allowed
      // All other triggers should be disabled
      if (!this.isProgram) return false;
      
      // t8 is Manual trigger - it's the only one allowed for programs
      return triggerId !== 't8';
    },
    
    // VueFlow methods
    zoomIn() {
      this.vueFlowZoomIn({ duration: 200 });
    },
    
    zoomOut() {
      this.vueFlowZoomOut({ duration: 200 });
    },
    
    autoLayout() {
      if (this.nodes.length === 0) {
        notify({
          title: 'Info',
          message: 'No nodes to layout',
          type: 'info',
          showClose: true
        });
        return;
      }

      // Create a new directed graph
      const g = new dagre.graphlib.Graph();
      
      // Set graph options for better vertical flow with minimal horizontal spreading
      g.setGraph({
        rankdir: 'TB', // Top to Bottom layout
        align: 'DL', // Align down-left to minimize horizontal spreading
        nodesep: 100, // Reduced horizontal spacing to keep nodes closer
        ranksep: 300, // Vertical spacing between levels
        edgesep: 20, // Spacing between edges
        marginx: 80,
        marginy: 80,
        ranker: 'tight-tree', // Use tight-tree ranker to minimize width
        acyclicer: 'greedy'
      });
      
      // Set default edge options
      g.setDefaultEdgeLabel(() => ({}));
      
      // Add nodes to the graph with their dimensions
      // All nodes have same width but use unconfigured height (larger) for layout spacing
      this.nodes.forEach(node => {
        // All nodes have same width (280px)
        const width = 280;
        
        // Use fixed unconfigured height for layout calculation
        // Configured nodes are actually smaller, so we use the larger unconfigured size
        let height = 150; // Default height
        
        // Trigger nodes - use unconfigured height
        if (this.isTriggerNodeType(node.type)) {
          height = 150; // Fixed unconfigured height
        }
        // Action nodes - use unconfigured height
        else if (this.isActionNodeType(node.type)) {
          height = 500; // Fixed unconfigured height (larger than configured)
        }
        
        g.setNode(node.id, { width, height });
      });
      
      // Add edges to the graph
      this.edges.forEach(edge => {
        g.setEdge(edge.source, edge.target);
      });
      
      // Run the dagre layout algorithm
      dagre.layout(g);
      
      // Update node positions based on layout
      this.nodes = this.nodes.map(node => {
        const nodeWithPosition = g.node(node.id);
        
        return {
          ...node,
          position: {
            // Dagre centers nodes, so we need to adjust for top-left positioning
            x: nodeWithPosition.x - nodeWithPosition.width / 2,
            y: nodeWithPosition.y - nodeWithPosition.height / 2
          }
        };
      });
      
      // Fit the view to show all nodes with some padding
      this.$nextTick(() => {
        this.setViewport({ x: 50, y: 50, zoom: 0.8 });
      });
      
      notify({
        title: 'Success',
        message: 'Layout applied successfully',
        type: 'success',
        duration: 2000,
        showClose: true
      });
      console.log('Auto-layout completed');
    },
    
    onNodesChange(changes) {
      // Apply node changes (move, remove, select, etc.)
      this.nodes = applyNodeChanges(changes, this.nodes);
    },
    
    onEdgesChange(changes) {
      // Check if any edges are being removed
      const removedEdges = changes.filter(change => change.type === 'remove');
      if (removedEdges.length > 0) {
        // Clear selectedNode and editingNode when edge is deleted
        this.selectedNode = null;
        this.editingNode = null;
      }
      
      this.edges = applyEdgeChanges(changes, this.edges);
    },
    
    deleteNode(nodeId) {
      // Prevent node deletion if canvas is disabled
      if (this.hasAssignees) {
        notify({
          title: 'Canvas Locked',
          message: 'Cannot delete nodes. This workflow has active patient assignments.',
          type: 'warning',
          duration: 3000,
          showClose: true
        });
        return;
      }

      // Prevent node deletion during AI generation or preview
      if (this.isAIGenerating || this.showPreview) {
        notify({
          title: 'Canvas Locked',
          message: 'Cannot delete nodes during AI generation or preview.',
          type: 'warning',
          duration: 3000,
          showClose: true
        });
        return;
      }
      
      // Find the node to get its label
      const node = this.nodes.find(n => n.id === nodeId);
      this.nodeToDelete = {
        id: nodeId,
        name: node?.data?.label || 'Node'
      };
      this.showDeleteNodeDialog = true;
    },
    
    confirmDeleteNode() {
      if (!this.nodeToDelete) return;
      
      // Find the node being deleted to check if it's a trigger
      const nodeBeingDeleted = this.nodes.find(n => n.id === this.nodeToDelete.id);
      const isTriggerNode = nodeBeingDeleted && this.isTriggerNodeType(nodeBeingDeleted.type);
      
      // Remove the node
      this.nodes = this.nodes.filter(node => node.id !== this.nodeToDelete.id);
      
      // Remove all edges connected to this node
      this.edges = this.edges.filter(edge => 
        edge.source !== this.nodeToDelete.id && edge.target !== this.nodeToDelete.id
      );
      
      // Clear selectedNode and editingNode
      this.selectedNode = null;
      this.editingNode = null;
      
      // If a trigger node was deleted, clear selectedTrigger to enable all triggers again
      if (isTriggerNode) {
        this.selectedTrigger = null;
      }
      
      // Reset
      this.nodeToDelete = null;
    },
    
    onConnect(params) {
      // Prevent connection during AI generation or preview
      if (this.isAIGenerating || this.showPreview) {
        return;
      }

      // Find the target node
      const targetNode = this.nodes.find(n => n.id === params.target);
      
      // Check if target is an action card using helper method
      if (targetNode && this.isActionNodeType(targetNode.type)) {
        // Check if target already has an incoming connection
        const hasIncomingConnection = this.edges.some(edge => edge.target === params.target);
        
        if (hasIncomingConnection) {
          // Block the connection and show notification
          notify({
            title: 'Connection Limit Reached',
            message: 'Action cards can only have one incoming connection.',
            type: 'warning',
            duration: 3000
          });
          return; // Block the connection
        }
      }
      
      // Get edge data template from NodeEdgeTemplate.json
      const template = NodeEdgeTemplate.edges.logicConnection;
      const edgeData = JSON.parse(JSON.stringify(template));
      
      // Create a new edge when nodes are connected using customized connection
      const newEdge = {
        id: `edge-${this.generateRandomId()}`,
        source: params.source,
        target: params.target,
        sourceHandle: params.sourceHandle,
        targetHandle: params.targetHandle,
        type: 'logicConnection',
        animated: false,
        data: edgeData
      };
      
      this.edges.push(newEdge);
    },
    
    /**
     * Handle workflow status change (activate/deactivate)
     * Called when user toggles the Draft/Active switch
     * 
     * @param {Object} data - Status change data
     * @param {string} data.status - New status ('active' or 'draft')
     * @param {Object} data.data - API response data
     */
    handleWorkflowStatusChanged(data) {
      console.log('Workflow status changed:', data);

      // Update local workflow data with new status
      if (this.originalWorkflowData) {
        this.originalWorkflowData.status = data.status;
      }
    },

    /**
     * Handle save workflow before activation
     * Called by ManageUsageDialog before validating and activating the workflow
     * This ensures all unsaved changes are persisted before validation
     *
     * @param {Object} callbacks - Callback functions for success/error
     * @param {Function} callbacks.onSuccess - Success callback to call after save
     * @param {Function} callbacks.onError - Error callback to call on save failure
     */
    async handleSaveWorkflowBeforeActivate({ onSuccess, onError }) {
      try {
        console.log('Saving workflow before activation...');

        // Call the existing saveWorkflow method
        const wasSaved = await this.saveWorkflow();

        if (!wasSaved) {
          if (onError) {
            onError(new Error('Failed to save workflow before activation'));
          }
          return;
        }

        // Call success callback to proceed with validation/activation
        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {
        console.error('Error saving workflow before activation:', error);

        // Call error callback to halt activation
        if (onError) {
          onError(error);
        }
      }
    },
    
    /**
     * Handle Manage Usage Save - Called ONLY when user clicks "Save Changes" button
     * Saves assignee changes to backend
     * 
     * @param {Object} data - All workflow usage data from ManageUsageDialog
     * @param {boolean} data.isActive - Workflow status (Draft/Active)
     * @param {Array} data.patients - Array of assigned patients with their states
     * @param {Object} data.response - API response from save assignees
     */
    handleManageUsageSave(data) {
      console.log('Manage Usage Data saved:', data);

      // Update originalWorkflowData with new assignment count
      // This will trigger the hasAssignees computed property to update
      if (this.originalWorkflowData && data.patients) {
        // Direct assignment works in Vue 3 (automatic reactivity)
        this.originalWorkflowData.assigned_patients_count = data.patients.length;

        console.log('Updated assigned_patients_count:', this.originalWorkflowData.assigned_patients_count);
        console.log('hasAssignees will now be:', data.patients.length > 0);
      }
    },

    /**
     * Handle click on Manage Assignees/Usage button
     * Opens the appropriate dialog based on isProgram
     */
    handleManageButtonClick() {
      if (this.isProgram) {
        this.showManageAssigneesDialog = true;
      } else {
        this.showManageUsageDialog = true;
      }
    },

    /**
     * Handle assignee data saved for program
     */
    handleManageAssigneesSave(data) {
      console.log('Manage Assignees Data saved:', data);

      // Update originalWorkflowData with new assignee count
      // This will trigger the hasAssignees computed property to update
      if (this.originalWorkflowData && data.patients) {
        // Use the patients array from the dialog (real-time tracking)
        const assigneeCount = data.patients.length;
        
        console.log('Program assignee count:', assigneeCount);
        
        // Direct assignment works in Vue 3 (automatic reactivity)
        this.originalWorkflowData.assigned_patients_count = assigneeCount;
        
        console.log('Updated assigned_patients_count:', this.originalWorkflowData.assigned_patients_count);
        console.log('hasAssignees will now be:', assigneeCount > 0);
      }
    },

    runTest() {
      // Expand test panel if collapsed
      if (this.$refs.testPanel && this.$refs.testPanel.isCollapsed) {
        this.$refs.testPanel.isCollapsed = false;
      }
      
      // Trigger test execution
      this.isTestRunning = true;
      // Reset after triggering
      setTimeout(() => {
        this.isTestRunning = false;
      }, 100);
    },
    
    handleTestPanelToggle(isOpen) {
      console.log('Test panel toggled:', isOpen);
    },
    
    handleRightPanelToggle(isOpen) {
      console.log('Right panel toggled:', isOpen);
    },
    
    onNodeClick(event) {
      // Prevent node selection and editor opening if canvas is disabled
      if (this.hasAssignees) {
        notify({
          title: 'Canvas Locked',
          message: 'Cannot edit nodes. This workflow has active patient assignments.',
          type: 'warning',
          duration: 3000,
          showClose: true
        });
        return;
      }

      // Prevent node selection during AI generation or preview
      if (this.isAIGenerating || this.showPreview) {
        notify({
          title: 'Canvas Locked',
          message: 'Cannot edit nodes during AI generation or preview.',
          type: 'warning',
          duration: 3000,
          showClose: true
        });
        return;
      }
      
      const node = event.node;
      
      // Define action node types
      const actionNodeTypes = [
        'sendNotification',
        'assignTask',
        'appointment',
        'sendDocuments',
        'placeOrder',
        'addInternalNotes'
      ];
      
      console.log('Node clicked:', node);
      // Check if this is an action node
      const isActionNode = actionNodeTypes.includes(node.type);
      
      // Check if the node is configured
      const isConfigured = node.data && node.data.configured === true;
      
      // If it's a configured action node, only select it (don't open editor)
      // User must click the edit button to open the editor
      this.selectedNode = node;
      if (isActionNode && isConfigured) {
        console.log('Configured action node selected (editor not opened):', node);
      } else {
        // For all other cases, open the editor
        this.editingNode = node;
        this.$refs.rightPanel.switchToEditorTab();
        console.log('Node selected and editor opened:', node);
      }
    },
    
    onPaneClick() {
      this.selectedNode = null;
      this.editingNode = null;
      console.log('Node deselected');
    },
    
    handleSaveConfiguration({ nodeId, data }) {
      
      // Store the saved configuration in the savedConfigurations array
      this.storeSavedConfiguration(nodeId, data);
      
      // Create savedData by deep cloning data and removing any nested savedData
      const savedData = JSON.parse(JSON.stringify(data));
      if (savedData.savedData) {
        delete savedData.savedData;
      }
      
      // Check if this is a wait logic (pseudo-node with _edgeId)
      if (nodeId.includes('edge-')) {
        const edgeIndex = this.edges.findIndex(e => e.id === nodeId);
        if (edgeIndex !== -1) {
          // Update edge data and add savedData field
          this.edges[edgeIndex].data = {
            ...data,
            savedData: savedData
          };
        }
      } else {
        // Regular node save - merge with existing data to preserve fields like subtype
        const nodeIndex = this.nodes.findIndex(n => n.id === nodeId);
        if (nodeIndex !== -1) {
          // Extract configured value first to ensure it's boolean
          const configuredValue = typeof data.configured === 'boolean' ? data.configured : false;
          
          // Merge new data with existing data to preserve all fields
          this.nodes[nodeIndex].data = {
            ...this.nodes[nodeIndex].data,  // Keep existing data
            ...data,                         // Merge new configuration
            configured: configuredValue,     // Explicitly set configured as boolean (overrides any wrong value from data)
            savedData: savedData
          };
        }
      }
      
      // Show success notification
      notify({
        title: 'Success',
        message: 'Configuration is saved',
        type: 'success',
        duration: 2000,
        showClose: true
      });
    },
    
    storeSavedConfiguration(id, data) {
      // Determine if this is a node or edge
      const type = id.includes('edge-') ? 'edge' : 'node';
      
      // Check if this id already exists in savedConfigurations
      const existingIndex = this.savedConfigurations.findIndex(config => config.id === id);
      
      if (existingIndex !== -1) {
        // Replace existing saved data
        this.savedConfigurations[existingIndex] = {
          id,
          type,
          data: JSON.parse(JSON.stringify(data)) // Deep clone to avoid reference issues
        };
        console.log('Updated saved configuration for:', id);
      } else {
        // Add new saved configuration
        this.savedConfigurations.push({
          id,
          type,
          data: JSON.parse(JSON.stringify(data)) // Deep clone to avoid reference issues
        });
        console.log('Added new saved configuration for:', id);
      }
      
      console.log('Current savedConfigurations:', this.savedConfigurations);
    },

    handleNodeConfigure(nodeId) {
      // Prevent node configuration if canvas is disabled
      if (this.hasAssignees) {
        notify({
          title: 'Canvas Locked',
          message: 'Cannot configure nodes. This workflow has active patient assignments.',
          type: 'warning',
          duration: 3000,
          showClose: true
        });
        return;
      }

      // Prevent node configuration during AI generation or preview
      if (this.isAIGenerating || this.showPreview) {
        notify({
          title: 'Canvas Locked',
          message: 'Cannot configure nodes during AI generation or preview.',
          type: 'warning',
          duration: 3000,
          showClose: true
        });
        return;
      }
      
      console.log('Node configured:', nodeId);
      this.selectedNode = this.nodes.find(n => n.id === nodeId);
      this.editingNode = this.selectedNode;
      this.$refs.rightPanel.switchToEditorTab();
    },

    handleOpenProductMenu(nodeId) {
      // Prevent opening product menu if canvas is disabled
      if (this.hasAssignees) {
        notify({
          title: 'Canvas Locked',
          message: 'Cannot configure nodes. This workflow has active patient assignments.',
          type: 'warning',
          duration: 3000,
          showClose: true
        });
        return;
      }

      // Prevent opening product menu during AI generation or preview
      if (this.isAIGenerating || this.showPreview) {
        notify({
          title: 'Canvas Locked',
          message: 'Cannot configure nodes during AI generation or preview.',
          type: 'warning',
          duration: 3000,
          showClose: true
        });
        return;
      }
      
      const node = this.nodes.find(n => n.id === nodeId);
      if (node) {
        this.configOrderNodeData = node.data;
        this.showConfigOrderDialog = true;
      }
    },

    handleConfigOrderSave(items) {
      if (this.configOrderNodeData) {
        // Update the node data with the new items
        this.configOrderNodeData.items = items;
        this.configOrderNodeData.configured = true;
        
        // Clear the reference
        this.configOrderNodeData = null;
      }
      this.showConfigOrderDialog = false;
    },

    /**
     * Handle workflow type chip click
     * For program to workflow (downgrade): Skip validation, go directly to convert dialog
     * For workflow to program (upgrade): Show validation dialog first
     */
    handleWorkflowTypeChipClick() {
      console.log('Workflow type chip clicked');
      
      // No validation needed for any conversion - directly show convert dialog
      console.log('Opening convert dialog without validation');
      this.showConvertDialog = true;
    },

    /**
     * Handle workflow validation
     * After validation, open convert dialog
     */
    handleValidateWorkflow() {
      console.log('Workflow validated, opening convert dialog');
      // Close validation dialog
      this.showValidationDialog = false;
      // Open convert dialog
      this.showConvertDialog = true;
    },

    /**
     * Handle workflow conversion
     * Convert between workflow and program using API
     */
    async handleConvertWorkflow() {
      console.log('Converting workflow type, current isProgram:', this.isProgram);
      
      if (!this.workflowId) {
        notify({
          title: 'Error',
          message: 'Cannot convert: Workflow ID not found. Please save the workflow first.',
          type: 'error',
          duration: 3000,
          showClose: true
        });
        this.showConvertDialog = false;
        return;
      }

      try {
        let response;
        
        if (this.isProgram) {
          // Downgrade program to workflow
          response = await downgradeProgramToWorkflowApi(this.workflowId);
          console.log('Downgrade response:', response);
          
          // Update local state
          this.isProgram = false;
          
          notify({
            title: 'Success',
            message: 'Successfully converted to Workflow',
            type: 'success',
            duration: 3000,
            showClose: true
          });
        } else {
          // Upgrade workflow to program
          // TODO: Collect program configuration from dialog
          // For now, use default rolling duration
          const programData = {
            duration: {
              type: 'rolling',
              unit: 'days',
              value: 30
            },
            price: 0
          };
          
          response = await upgradeWorkflowToProgramApi(this.workflowId, programData);
          console.log('Upgrade response:', response);
          
          // Update local state
          this.isProgram = true;
          
          // Initialize program settings with current workflow title and description
          this.programSettings = {
            name: this.workflowTitle || '',
            description: this.workflowDescription || '',
            duration_type: 'rolling',
            start_date: '',
            end_date: '',
            durationValue: '30',
            durationUnit: 'days',
            maximum_enrollment: null,
            price: 0
          };
          
          notify({
            title: 'Success',
            message: 'Successfully converted to Program',
            type: 'success',
            duration: 3000,
            showClose: true
          });
        }
        
        // Switch right panel to Block Editor tab after conversion
        if (this.$refs.rightPanel) {
          this.$refs.rightPanel.activeTab = 'editor';
        }
        
        // Close convert dialog
        this.showConvertDialog = false;
        
      } catch (error) {
        console.error('Error converting workflow:', error);
        
        // Close convert dialog
        this.showConvertDialog = false;
        
        // Show error notification
        const errorMessage = error.response?.data?.message || error.message || 'Failed to convert workflow';
        notify({
          title: 'Conversion Failed',
          message: errorMessage,
          type: 'error',
          duration: 5000,
          showClose: true
        });
      }
    },

    handleLogicClick({ edgeId, logicType, edgeData }) {
      // Prevent logic configuration if canvas is disabled
      if (this.hasAssignees) {
        notify({
          title: 'Canvas Locked',
          message: 'Cannot configure logic blocks. This workflow has active patient assignments.',
          type: 'warning',
          duration: 3000,
          showClose: true
        });
        return;
      }

      // Prevent logic configuration during AI generation or preview
      if (this.isAIGenerating || this.showPreview) {
        notify({
          title: 'Canvas Locked',
          message: 'Cannot configure logic blocks during AI generation or preview.',
          type: 'warning',
          duration: 3000,
          showClose: true
        });
        return;
      }

      // Handle both wait and review logic clicks
      if (logicType !== 'wait' && logicType !== 'review') return;
      
      // Find the edge
      const edge = this.edges.find(e => e.id === edgeId);
      if (!edge) return;
      
      // Create a pseudo-node for the editor with edge data
      // This allows the editor to work with edge data as if it were a node
      const pseudoNode = {
        id: edgeId,
        type: logicType, // 'wait' or 'review'
        data: edge.data || {}
      };
      
      // Set as editing node to open in right panel
      this.selectedNode = pseudoNode;
      this.editingNode = pseudoNode;
      this.$refs.rightPanel.switchToEditorTab();
    },
    
    handleLogicCardDropped({ edgeId, logicCardType, logicCardConfigured }) {
      // Find the edge and update its data with the logic card information
      const edgeIndex = this.edges.findIndex(e => e.id === edgeId);
      if (edgeIndex === -1) {
        console.error('Edge not found:', edgeId);
        return;
      }
      
      // Get logic card data template from NodeEdgeTemplate.json
      const template = NodeEdgeTemplate.logicCards[logicCardType];
      if (!template) {
        console.error('No template found for logic card type:', logicCardType);
        return;
      }
      
      // Deep clone the template to avoid reference issues
      const logicCardData = JSON.parse(JSON.stringify(template));
      
      // Merge with existing edge data
      this.edges[edgeIndex].data = {
        ...this.edges[edgeIndex].data,
        ...logicCardData
      };
      
      console.log('Edge updated with logic card:', this.edges[edgeIndex]);
      
      // Show success notification
      notify({
        title: 'Success',
        message: `${logicCardType === 'wait' ? 'Wait' : 'Review & Approval'} logic block added to connection`,
        type: 'success',
        duration: 2000,
        showClose: true
      });
      
      // Automatically open the editor for the newly dropped logic card
      const edge = this.edges[edgeIndex];
      
      // Create a pseudo-node for the editor with edge data (same structure as handleLogicClick)
      const pseudoNode = {
        id: edge.id,
        type: logicCardType, // 'wait' or 'review'
        data: edge.data || {}
      };
      
      this.selectedNode = pseudoNode;
      this.editingNode = pseudoNode;
      this.$refs.rightPanel.switchToEditorTab();
      console.log('Editor opened for newly dropped logic card:', edgeId, 'type:', logicCardType);
    },

    handleAIGenerationStart() {
      // Lock the canvas during AI generation (thinking phase), but don't show overlay yet
      this.isAIGenerating = true;
    },

    handleAIGeneratingOverlayShow() {
      // Now show the overlay for the fake "generating" phase
      this.showAIOverlay = true;
      this.aiOverlayState = 'loading';
    },

    handleAIGenerationEnd() {
      // AI generation complete, re-enable canvas
      this.isAIGenerating = false;
    },

    handleAIGenerationError() {
      // Show error overlay and re-enable canvas
      this.isAIGenerating = false;
      this.aiOverlayState = 'error';
    },
    
    handleCloseOverlay() {
      // Close overlay and reset state
      this.showAIOverlay = false;
      this.aiOverlayState = 'loading';
    },

    handleAIWorkflowGenerated(workflowData) {
      console.log('AI Workflow Generated:', workflowData);
      
      // Store the old workflow before replacing
      const oldWorkflow = {
        nodes: JSON.parse(JSON.stringify(this.nodes)),
        edges: JSON.parse(JSON.stringify(this.edges)),
        title: this.workflowTitle,
        description: this.workflowDescription,
        timestamp: new Date().toISOString()
      };
      
      // Store in data for potential undo functionality
      this.previousWorkflow = oldWorkflow;
      
      // Replace current workflow with AI-generated workflow
      if (workflowData.definition) {
        // Update title and description if provided
        if (workflowData.name) {
          this.workflowTitle = workflowData.name;
        }
        if (workflowData.description) {
          this.workflowDescription = workflowData.description;
        }
        
        // If this is a program, update program settings with AI response data
        if (this.isProgram && workflowData.program_settings) {
          this.programSettings = {
            ...this.programSettings,
            name: workflowData.name || this.programSettings.name,
            description: workflowData.description || this.programSettings.description,
            duration_type: workflowData.program_settings.duration_type || this.programSettings.duration_type,
            start_date: workflowData.program_settings.start_date || this.programSettings.start_date,
            end_date: workflowData.program_settings.end_date || this.programSettings.end_date,
            duration_value: workflowData.program_settings.duration_value || this.programSettings.duration_value,
            duration_unit: workflowData.program_settings.duration_unit || this.programSettings.duration_unit,
            enrollment_control: workflowData.program_settings.enrollment_control || this.programSettings.enrollment_control,
            min_enrollment: workflowData.program_settings.min_enrollment || this.programSettings.min_enrollment,
            max_enrollment: workflowData.program_settings.max_enrollment || this.programSettings.max_enrollment,
            price: workflowData.program_settings.price || this.programSettings.price
          };
        }
        
        // Convert AI workflow nodes to VueFlow format
        const newNodes = this.convertAIWorkflowToNodes(workflowData.definition);
        const newEdges = this.convertAIWorkflowToEdges(workflowData.definition);
        
        // Replace nodes and edges
        this.nodes = newNodes;
        this.edges = newEdges;
        
        // Clear selected nodes
        this.selectedNode = null;
        this.editingNode = null;
        
        console.log('Workflow replaced with AI-generated workflow');
        console.log('Previous workflow stored for undo');
        
        // Hide loading overlay
        this.showAIOverlay = false;
        
        // Auto layout the workflow before showing preview
        this.$nextTick(() => {
          this.autoLayout();
          
          // Fit view to show all workflows after layout
          this.$nextTick(() => {
            this.fitView({ padding: 0.2, duration: 500 });
            
            // Show preview after viewport is adjusted
            setTimeout(() => {
              this.showPreview = true;
            }, 500);
          });
        });
      }
    },
    
    focusOnTrigger() {
      // Find the trigger node using helper method
      const triggerNode = this.nodes.find(node => this.isTriggerNodeType(node.type));
      
      if (triggerNode) {
        // Use setViewport to center on trigger node
        const centerX = triggerNode.position.x + 110; // Half of node width
        const centerY = triggerNode.position.y + 75; // Half of node height
        
        this.setViewport({
          x: window.innerWidth / 2 - centerX,
          y: window.innerHeight / 2 - centerY,
          zoom: 1
        }, { duration: 800 });
      }
    },
    
    handleDiscardAIWorkflow() {
      // Restore previous workflow
      if (this.previousWorkflow) {
        this.nodes = JSON.parse(JSON.stringify(this.previousWorkflow.nodes));
        this.edges = JSON.parse(JSON.stringify(this.previousWorkflow.edges));
        this.workflowTitle = this.previousWorkflow.title;
        this.workflowDescription = this.previousWorkflow.description;

        console.log('AI workflow discarded, previous workflow restored');
      }

      // Hide preview
      this.showPreview = false;

      // End AI generation state
      this.isAIGenerating = false;
    },
    
    async handleAcceptAIWorkflow() {
      // Save the workflow via API
      try {
        await this.saveWorkflow();

        // Hide preview
        this.showPreview = false;

        // End AI generation state
        this.isAIGenerating = false;

        console.log('AI workflow accepted and saved');
      } catch (error) {
        console.error('Failed to save AI workflow:', error);
        // Show error notification but keep preview
      }
    },
    
    convertAIWorkflowToNodes(definition) {
      // Import convertBackendToFrontend from workflowManage.js
      const { convertBackendToFrontend } = require('@/api/workflowManage');
      
      // Use the same conversion logic as loading workflows from backend
      const converted = convertBackendToFrontend({
        definition: definition
      });
      
      // Store logic node map for edge reconstruction
      this._logicNodeMap = converted.logicNodeMap || new Map();
      
      // Position nodes vertically with spacing
      let yPosition = 100;
      const nodes = converted.nodes.map(node => {
        const positionedNode = {
          ...node,
          position: { x: 200, y: yPosition }
        };
        yPosition += 200;
        return positionedNode;
      });
      
      return nodes;
    },
    
    convertAIWorkflowToEdges(definition) {
      // Import convertBackendToFrontend from workflowManage.js
      const { convertBackendToFrontend } = require('@/api/workflowManage');
      
      // Use the same conversion logic as loading workflows from backend
      const converted = convertBackendToFrontend({
        definition: definition
      });
      
      // Return the converted edges
      return converted.edges;
    },
    
    // Old implementation removed - now using convertBackendToFrontend
    _oldConvertAIWorkflowToEdges_REMOVED(definition) {
      const connections = definition.connections || [];
      connections.forEach((connection, index) => {
        const { source_node_id, target_node_id } = connection;
        
        // Check if this connection involves a logic node
        const sourceLogic = logicNodeMap.get(source_node_id);
        const targetLogic = logicNodeMap.get(target_node_id);
        
        if (sourceLogic || targetLogic) {
          // This connection involves a logic node - need to reconstruct as edge with logic
          let actualSource = source_node_id;
          let actualTarget = target_node_id;
          let logicData = null;
          
          // If source is logic, find what connects TO the logic node
          if (sourceLogic) {
            const incomingConn = connections.find(c => c.target_node_id === source_node_id);
            if (incomingConn) {
              actualSource = incomingConn.source_node_id;
              logicData = sourceLogic;
            }
          }
          
          // If target is logic, find what connects FROM the logic node
          if (targetLogic) {
            const outgoingConn = connections.find(c => c.source_node_id === target_node_id);
            if (outgoingConn) {
              actualTarget = outgoingConn.target_node_id;
              logicData = targetLogic;
            }
          }
          
          // Create connection key to avoid duplicates
          const connectionKey = `${actualSource}-${actualTarget}`;
          if (processedConnections.has(connectionKey)) {
            return; // Skip duplicate
          }
          processedConnections.add(connectionKey);
          
          // Create edge with logic data
          const edgeId = `edge-${actualSource}-${actualTarget}`;
          const edge = {
            id: edgeId,
            source: actualSource,
            target: actualTarget,
            type: 'logicConnection',
            data: {
              logicCardType: null,
              logicCardConfigured: false,
              logicCardData: null
            }
          };
          
          // Add logic configuration to edge data
          if (logicData) {
            const config = logicData.config || {};
            
            if (logicData.type === 'approval') {
              // Map backend field names to frontend for review/approval logic
              edge.data = {
                logicCardType: 'review',
                logicCardConfigured: true,
                logicCardData: {
                  primaryReviewer: config.primaryReviewer || '',
                  hasEscalation: config.hasEscalateReviewer || false,
                  escalationReviewer: config.escalateReview || '',
                  reviewResponseTime: config.responseTime?.value || 0,
                  reviewResponseTimeUnit: config.responseTime?.unit || 'hours',
                  actionOnTimeout: config.actionOnTimeout || '',
                  hasAIPreScreening: config.useAIReview || false,
                  approvalInstructions: config.AIReviewInstruction?.approvalInstruction || '',
                  rejectionInstructions: config.AIReviewInstruction?.rejectionInstruction || '',
                  escalationInstructions: config.AIReviewInstruction?.escalationInstruction || ''
                }
              };
            } else if (logicData.type === 'wait') {
              // Map backend field names to frontend for wait logic
              edge.data = {
                logicCardType: 'wait',
                logicCardConfigured: true,
                logicCardData: {
                  waitTime: config.duration?.value || 1,
                  waitTimeUnit: (config.duration?.unit || 'minutes').charAt(0).toUpperCase() + (config.duration?.unit || 'minutes').slice(1)
                }
              };
            }
            
            // Create savedData by copying edge.data and removing nested savedData
            const edgeSavedData = JSON.parse(JSON.stringify(edge.data));
            if (edgeSavedData.savedData) {
              delete edgeSavedData.savedData;
            }
            
            // Add savedData to edge.data
            edge.data.savedData = edgeSavedData;
          }
          
          edges.push(edge);
        } else {
          // Regular connection without logic
          const edge = {
            id: `edge-${source_node_id}-${target_node_id}`,
            source: source_node_id,
            target: target_node_id,
            type: 'logicConnection',
            data: {
              logicCardType: null,
              logicCardConfigured: false,
              logicCardData: null
            }
          };
          
          edges.push(edge);
        }
      });
      
      // Clean up temporary storage
      delete this._logicNodeMap;
      
      return edges;
    },
    
    handleRemoveLogicBlock(edgeId) {
      // Find the edge
      const edgeIndex = this.edges.findIndex(e => e.id === edgeId);
      if (edgeIndex === -1) {
        console.error('Edge not found:', edgeId);
        return;
      }
      
      // Get the default edge template from NodeEdgeTemplate.json
      const template = NodeEdgeTemplate.edges.logicConnection;
      const edgeData = JSON.parse(JSON.stringify(template));
      
      // Reset edge data to initial state (logicCardType: null, logicCardConfigured: false, logicCardData: null)
      this.edges[edgeIndex].data = edgeData;
      
      // Clear selectedNode and editingNode
      this.selectedNode = null;
      this.editingNode = null;
      
      console.log('Logic block removed from edge, reset to initial state:', this.edges[edgeIndex]);
      
      // Show success notification
      notify({
        title: 'Success',
        message: 'Logic block removed. Drop zone is ready for a new logic block.',
        type: 'success',
        duration: 2000,
        showClose: true
      });
    }

  }
};
</script>

<style scoped src="./WorkflowCanvas.css"></style>

<style>
/* VueFlow Core Styles - Must be unscoped */
@import '@vue-flow/core/dist/style.css';
@import '@vue-flow/core/dist/theme-default.css';
@import '@vue-flow/controls/dist/style.css';
</style>
